import type { FunctionComponent } from 'react'

import { useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import {
	BoxInput,
	Chip,
	CurrencyDisplay,
	DateTime,
	IconButton,
	MaterialIcons,
	useIntlConfig,
	useModal,
} from '@monthio/react-components'
import React, { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type {
	DebtPaymentDto,
	RevampedDebtRecordDto,
} from '../../types/debtOverviewApi.types'
import type { DebtItem, DeleteReason } from '../debtItem'

import {
	RowsWithHeadingContainer,
	RowWithHeading,
} from '../../../../modulesTemp/general/FlowWizardUtils'
import { Conditionally } from '../../../../modulesTemp/shared/conditionally'
import { ConditionalWrapper } from '../../../../modulesTemp/shared/conditionalWrapper'
import { DeletedOverlay } from '../../../../modulesTemp/shared/deletedOverlay'
import { ItemWithOptionalLogo } from '../../../../modulesTemp/shared/ItemWithOptionalLogo'
import { DebtRecordRevampedType } from '../../types/debtOverviewApi.types'
import { debtFallbackImageSrc } from '../debtFallbackImage'
import { canonicalDebtRecord, isDebtDeleted } from '../debtItem'
import {
	useDebtAtCreditorText,
	useDebtTypeText,
} from '../debtOverviewUtilities'
import { DebtTypeSelect } from '../DebtTypeSelect'
import { DeleteModal } from '../DeleteModal'
import { ReactivateDebtMessage } from '../reactivateDebtMessage'
import { useCurrencySymbol } from '../useCurrencySymbol'
import { DebtItemErrors } from './DebtItemErrors'

export type UpdateDebtItem = (
	id: string,
	creditorId: number | null,
	value:
		| {
				type: 'installment'
				value: number
				debtRecordIndex?: never
				paymentId?: string
		  }
		| {
				type: 'outstanding'
				debtRecordIndex: number
				value: number
				paymentId?: string
		  }
		| {
				type: 'debtType'
				value: DebtRecordRevampedType
				debtRecordIndex?: never
				paymentId?: string
		  },
) => void

export type DeleteDebtItem = (
	id: string,
	action: DeleteReason | 'un-delete' | 'remove',
) => void

export interface DebtPayment extends DebtPaymentDto {
	debtId?: string
}

export const DateView: React.FC<{ date: string | null }> = ({ date }) => {
	if (!date) {
		return null
	}

	const [datePart, timePart] = date.split('T')
	return (
		<DateTime
			date={`${datePart}T${timePart || '00:00:00'}`} // Ensure compatability with poor safary
			format="full"
			showYear
		/>
	)
}

const currencyInputProps = {
	decimalScale: 0,
	decimalsLimit: 0,
	maxLength: 8,
	allowNegativeValue: false,
}

const Creditor: FunctionComponent<{
	creditor: string
	description: string | null
	logoSrc?: string
	logo?: JSX.Element
	onDelete: () => void
	children?: never
}> = ({ creditor, description, logo, logoSrc, onDelete }) => (
	<ItemWithOptionalLogo
		title={creditor}
		description={description}
		logoSrc={logoSrc}
		logo={logo}
	>
		<IconButton onClick={onDelete}>
			<MaterialIcons.Delete />
		</IconButton>
	</ItemWithOptionalLogo>
)

const ResponsiveReactivateDebtMessage = styled(ReactivateDebtMessage)`
	margin-bottom: 40px;

	${({ theme }) => theme.breakpoints.up('sm')} {
		align-self: center;
	}
`

const DebtPaymentSuggestionsContainer = styled.div`
	padding: 24px 0px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`

const DebtPaymentsSuggestionsChipContainer = styled.div`
	display: flex;
	flex-direction: row;
	gap: 16px;
`

const calculatePrincipalAmount = (
	total: number,
	debtRecord: RevampedDebtRecordDto,
) => total + (debtRecord.principalAmount ?? 0)

export const DebtOverviewCard: FunctionComponent<{
	debtItem: DebtItem
	updateItem: UpdateDebtItem
	deleteItem: DeleteDebtItem
	suggestedDebtPayments: DebtPayment[]
	updateSuggestedDebtPayment: (
		debtPaymentId: string,
		updatedDebtPayment: DebtPayment,
	) => void
}> = ({
	debtItem,
	updateItem,
	deleteItem,
	suggestedDebtPayments,
	updateSuggestedDebtPayment,
}) => {
	const { t } = useTranslation()
	const debtTypeText = useDebtTypeText()
	const debtAtCreditorTypeText = useDebtAtCreditorText()
	const { openModal } = useModal()
	const theme = useTheme()
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
	const intlConfig = useIntlConfig({ showCurrency: true })

	const showSuggestedDebtPayments = useRef<boolean>(
		(debtItem.debtPayment.paymentAmount == undefined ||
			debtItem.debtPayment.paymentAmount === 0) &&
			suggestedDebtPayments.length !== 0,
	)

	const { creditor, context, debtPayment, debtRecords } = debtItem
	const record = canonicalDebtRecord(debtItem)

	const onDelete = useCallback(async () => {
		if (context === 'CREATED_BY_USER') {
			return deleteItem(debtItem.id, 'remove')
		}
		if (record.status && record.status !== 'active') {
			return deleteItem(debtItem.id, 'un-delete')
		}
		const reason = await openModal({
			bodyRender: DeleteModal,
			fullScreen: isSmallScreen,
			title: t('debtOverview-deleteModal-title'),
		})
		reason && deleteItem(debtItem.id, reason)
	}, [
		context,
		record.status,
		openModal,
		isSmallScreen,
		t,
		deleteItem,
		debtItem.id,
	])

	const isDeleted = isDebtDeleted(record)
	const currencySymbol = useCurrencySymbol(debtPayment.currency)
	const principalAmount = debtRecords.reduce(calculatePrincipalAmount, 0)

	const handleChipClick = useCallback(
		(debtPayment: DebtPayment) => {
			updateItem(debtItem.id, record.monthioCreditorId, {
				type: 'installment',
				value: Math.round(debtPayment.paymentAmount ?? 0),
				paymentId: debtPayment.id,
			})

			// Unassign debt from other suggestedDebtPayments
			suggestedDebtPayments
				.filter((debtPayment) => debtPayment.debtId === debtItem.id)
				.forEach((debtPayment) => {
					debtPayment.debtId = undefined
					updateSuggestedDebtPayment(debtPayment.id, debtPayment)
				})

			debtPayment.debtId = debtItem.id
			updateSuggestedDebtPayment(debtPayment.id, debtPayment)
		},
		[
			debtItem.id,
			record.monthioCreditorId,
			suggestedDebtPayments,
			updateItem,
			updateSuggestedDebtPayment,
		],
	)

	const uniqueSuggestedDebtPayments = [
		...new Map(
			suggestedDebtPayments
				.filter((debtPayment) => debtPayment.debtId === undefined)
				.map((debtPayment) => [debtPayment.paymentAmount, debtPayment]),
		).values(),
	] as DebtPaymentDto[]

	const FallbackCreditorLogo = debtFallbackImageSrc(
		record.debtType || DebtRecordRevampedType.Unknown,
	)
	return (
		<>
			<Conditionally condition={isDeleted}>
				<ResponsiveReactivateDebtMessage reactivate={onDelete} />
			</Conditionally>
			<ConditionalWrapper condition={isDeleted} Wrapper={DeletedOverlay}>
				<RowsWithHeadingContainer>
					{debtItem.context === 'USER_IS_ADDING' ? (
						<RowWithHeading
							heading={t('debtOverview-add-debt-loan-type')}
							tooltip={t('debtOverview-loan-type-tool-tip-text')}
						>
							<DebtTypeSelect
								value={record.debtType}
								onChange={(value) =>
									updateItem(debtItem.id, record.monthioCreditorId, {
										type: 'debtType',
										value,
										paymentId: debtPayment.id,
									})
								}
							/>
						</RowWithHeading>
					) : (
						<Creditor
							creditor={
								creditor?.name
									? debtAtCreditorTypeText(record.debtType, creditor.name)
									: debtTypeText(record.debtType)
							}
							description={record.description}
							logoSrc={creditor?.logo}
							logo={<FallbackCreditorLogo viewBox="10 10 60 60" />}
							onDelete={onDelete}
						/>
					)}
					{debtRecords.length > 1 ? (
						<RowWithHeading
							heading={t('debtOverview-outstanding-amount', {
								count: debtRecords.length,
							})}
							tooltip={t('debtOverview-outstanding-amount-tooltip', {
								count: debtRecords.length,
							})}
						>
							<BoxInput
								value={principalAmount}
								currencyInputProps={currencyInputProps}
								unit={currencySymbol}
								disabled
							/>
						</RowWithHeading>
					) : null}
					{debtRecords.map((debtRecord, debtRecordIndex) => (
						<RowWithHeading
							key={`outstanding-${debtItem.id}:${debtRecord.id}:${debtRecordIndex}`}
							heading={`${
								debtRecords.length > 1
									? `${debtTypeText(record.debtType)} ${debtRecordIndex + 1} `
									: ''
							}${t('debtOverview-outstanding-amount')}`}
							tooltip={
								debtRecords.length > 1 ? (
									<>
										{`${t('debtOverview-start-date')} `}
										<DateView date={debtRecords[0].loanStartDate} />
									</>
								) : (
									t('debtOverview-outstanding-amount-tooltip')
								)
							}
						>
							<BoxInput
								error={
									!isDeleted &&
									(!debtRecord.principalAmount ||
										debtRecord.principalAmount < 0)
								}
								value={debtRecord.principalAmount}
								onChange={(e) =>
									updateItem(debtItem.id, record.monthioCreditorId, {
										type: 'outstanding',
										debtRecordIndex,
										value: e.target.value as unknown as number,
										paymentId: debtPayment.id,
									})
								}
								currencyInputProps={currencyInputProps}
								unit={currencySymbol}
							/>
						</RowWithHeading>
					))}

					<RowWithHeading
						heading={t('debtOverview-monthly-installment')}
						tooltip={t('debtOverview-monthly-installment-tooltip')}
					>
						<BoxInput
							key={`monthly-${debtItem.id}`}
							error={
								!isDeleted &&
								(debtPayment.paymentAmount == null ||
									debtPayment.paymentAmount < 0)
							}
							value={debtPayment.paymentAmount}
							onChange={(e) =>
								updateItem(
									debtItem.id,
									canonicalDebtRecord(debtItem).monthioCreditorId,
									{
										type: 'installment',
										value: e.target.value as unknown as number,
										paymentId: debtPayment.id,
									},
								)
							}
							currencyInputProps={currencyInputProps}
							unit={currencySymbol}
						/>
					</RowWithHeading>
					<Conditionally
						condition={
							debtRecords.length === 1 && debtRecords[0].loanStartDate != null
						}
					>
						<RowWithHeading
							heading={t('debtOverview-start-date')}
							tooltip={t('debtOverview-start-date-tooltip')}
						>
							<DateView date={debtRecords[0].loanStartDate} />
						</RowWithHeading>
					</Conditionally>
				</RowsWithHeadingContainer>
				<Conditionally
					condition={
						showSuggestedDebtPayments.current &&
						uniqueSuggestedDebtPayments.length > 0
					}
				>
					<DebtPaymentSuggestionsContainer>
						<div>{t('debtOverview-suggestedExpenses-text')}</div>
						<DebtPaymentsSuggestionsChipContainer>
							{uniqueSuggestedDebtPayments.map((debtPayment, i) => (
								<Chip
									key={`SUGGESTED_DEBT_PAYMENT::${i}`}
									label={
										<CurrencyDisplay
											amount={Math.round(debtPayment.paymentAmount ?? 0)}
											currencyFormatOptions={{
												allowNegativeValue: false,
												decimalsLimit: 0,
												intlConfig,
												maxLength: 8,
											}}
										/>
									}
									color="primary"
									onClick={() => handleChipClick(debtPayment)}
								/>
							))}
						</DebtPaymentsSuggestionsChipContainer>
					</DebtPaymentSuggestionsContainer>
				</Conditionally>
				<Conditionally condition={suggestedDebtPayments.length == 0}>
					<DebtItemErrors item={debtItem} />
				</Conditionally>
			</ConditionalWrapper>
		</>
	)
}
